// ============================================================================
// API CLIENT
// ============================================================================

const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api'

// Types
export interface User {
  id: string
  email: string
  name: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface ApiError {
  detail: string
}

// Token management
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('access_token')
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('refresh_token')
}

export function setTokens(tokens: TokenResponse): void {
  localStorage.setItem('access_token', tokens.access_token)
  localStorage.setItem('refresh_token', tokens.refresh_token)
}

export function clearTokens(): void {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

// API request helper
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_URL}${endpoint}`
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Add auth header if token exists
  const token = getAccessToken()
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // Handle 401 - try to refresh token
  if (response.status === 401 && getRefreshToken()) {
    const refreshed = await refreshTokens()
    if (refreshed) {
      // Retry with new token
      (headers as Record<string, string>)['Authorization'] = `Bearer ${getAccessToken()}`
      const retryResponse = await fetch(url, { ...options, headers })
      if (!retryResponse.ok) {
        const error = await retryResponse.json().catch(() => ({ detail: 'Request failed' }))
        throw new Error(error.detail || 'Request failed')
      }
      return retryResponse.json()
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }))
    throw new Error(error.detail || 'Request failed')
  }

  return response.json()
}

// Refresh tokens
async function refreshTokens(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      clearTokens()
      return false
    }

    const tokens: TokenResponse = await response.json()
    setTokens(tokens)
    return true
  } catch {
    clearTokens()
    return false
  }
}

// ============================================================================
// AUTH API
// ============================================================================

export async function register(data: {
  email: string
  password: string
  name?: string
}): Promise<User> {
  return request<User>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function login(email: string, password: string): Promise<TokenResponse> {
  const response = await fetch(`${API_URL}/auth/login/json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Login failed' }))
    throw new Error(error.detail || 'Login failed')
  }

  const tokens: TokenResponse = await response.json()
  setTokens(tokens)
  return tokens
}

export async function logout(): Promise<void> {
  clearTokens()
}

export async function getCurrentUser(): Promise<User> {
  return request<User>('/auth/me')
}

export async function updateProfile(data: {
  name?: string
  email?: string
}): Promise<User> {
  return request<User>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function changePassword(data: {
  current_password: string
  new_password: string
}): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/me/password', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ============================================================================
// CONTACT / QUOTE API
// ============================================================================

export interface ContactPayload {
  name: string
  email: string
  message: string
  subject?: string
  company?: string
}

export interface MessageResponse {
  message: string
}

export async function submitContact(payload: ContactPayload): Promise<MessageResponse> {
  const response = await fetch(`${API_URL}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: 'تعذّر إرسال الطلب' }))
    throw new Error(
      typeof error.detail === 'string' ? error.detail : 'تعذّر إرسال الطلب'
    )
  }

  return response.json()
}
